/**
 * ModelRow — base class for every row. Barebones, tree-aware,
 * read-only at the base level.
 *
 * A row instance is a **shim** over the stores owned by its Sheet.
 * The row carries only its id + a reference to the Sheet; all
 * typed data access flows through the sheet's stores. The back-pointer
 * is an ECMAScript `#` field: nothing outside this class body reaches it
 * (no bracket access, no subclass). The package's variants and modules
 * reach the full `Sheet` through the `sheetOf` friend door
 * (`../sheet/sheet-stores`), installed once from the static block below; the
 * row's public face onto its sheet is `sheetReads`, the rows-side reads.
 * No member of a `ModelRow` writes. Concrete
 * subclasses (variants) extend ModelRow (or one of the role
 * abstractions — SourceRow / LayoutRow / RendererRow / AliasRow)
 * and add type-specific read/write methods.
 *
 * Detail: `sheets/.docs/content/rows-sheet.md`.
 */

import type { Option } from "../core/option.ts";
import type { DateUnit, NaiveDate } from "@tento-chrono";

import type { Descriptor } from "./descriptor.ts";
import type { Graph } from "../stores/graph-types.ts";
import type { JsonObject } from "../core/json.ts";
import { EMPTY_FROZEN_OBJECT } from "../core/frozen-copy.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";
import type { ModelRowOptions } from "./row-options.ts";
import type { RowEnvelopes } from "./row-envelopes.ts";
import { type SheetModelReads, sheetReadsOf } from "../sheet/rows-sheet-view.ts";
import type { ModelSheet } from "../sheet/rows-sheet.ts";
import type { SourceRow } from "./variants/source-row.ts";
import { installRowFriend, sheetInternals, sheetStores } from "../sheet/sheet-stores.ts";
import { RowLoadPass } from "./row-load.ts";
import { faceRowOf, type ModelRowReads } from "./model-row-reads.ts";
import type { PropertyListProjection } from "../render/tree-projection.ts";

/**
 * Row-level capability flags — what operations the UI may surface.
 * Computed lazily from the descriptor + tree state.
 */
export interface Capabilities {
  readonly canReparent: boolean;
  readonly canDuplicate: boolean;
  readonly canDelete: boolean;
  readonly canReorder: boolean;
  readonly canEditHeight: boolean;
  readonly canEditTitle: boolean;
  /**
   * Whether the row's cells take a write. False for a row whose cells are
   * computed — a formula row, a row a client-side provider computes — whose
   * cell verbs (`@tento/lona`'s writable facades' `write`, `writeDurable`, `clear`,
   * `clearDurable`, `move`, `replace`, and the `writableSingle()` / `writableMulti()`
   * facades') refuse with `CellWriteError` (outcome `unaddressable`),
   * nothing sent. A surface reads it before offering an edit.
   */
  readonly canWriteCells: boolean;
}

/**
 * The base of every rows-side row: its id, descriptor, label, parent and
 * children, read through the sheet it belongs to.
 */
export class ModelRow {
  static {
    installRowFriend((row) => row.#sheet);
  }

  /**
   * The full rows-side model this row lives in, writers included — a `#`
   * field, so a row handed out never hands out its sheet. The package
   * reaches it through the `sheetOf` friend door; everyone else reads
   * `sheetReads`.
   */
  readonly #sheet: ModelSheet;

  /**
   * Descriptor is the row's **immutable** type definition — role,
   * dtype, schema, cardinality, etc. Mutable per-row state lives
   * in the sheet's row-metadata store.
   */
  constructor(
    readonly id: RowLocalId,
    readonly descriptor: Descriptor,
    sheet: ModelSheet,
  ) {
    this.#sheet = sheet;
    // Frozen as the row takes it: every reader (the row's reads face, a
    // render front-end) shares the one descriptor, and none can write it.
    Object.freeze(descriptor);
  }

  /**
   * The sheet this row lives in, as its reads (`SheetModelReads`): rows,
   * tree, metadata, cells, the timescale — the sheet's frozen reads face
   * (`sheetReadsOf`), so no writer is reachable from a row, by cast or
   * `instanceof`.
   */
  get sheetReads(): SheetModelReads {
    return sheetReadsOf(this.#sheet);
  }

  // ─── Tree (read-only) ──────────────────────────────────────────

  get parent(): Option<ModelRow> {
    const parentId = sheetStores(this.#sheet).tree.parent(this.id);
    return parentId == null ? null : this.#sheet.peekRow(parentId) ?? null;
  }

  get children(): readonly ModelRow[] {
    const childIds = sheetStores(this.#sheet).tree.children(this.id);
    const rows: ModelRow[] = [];
    for (const cid of childIds) {
      const row = this.#sheet.peekRow(cid);
      if (row) rows.push(row);
    }
    return rows;
  }

  // ─── Per-row metadata accessors ────────────────────────────────

  /** Label reconciled from `RowMetadataStore`; unset when the row has none. */
  get label(): Option<string> {
    return sheetStores(this.#sheet).rowMetadata.get(this.id).label ?? null;
  }

  /** Lookup key reconciled from `RowMetadataStore`; unset when the row has none. */
  get lookupKey(): Option<string> {
    return sheetStores(this.#sheet).rowMetadata.get(this.id).lookupKey ?? null;
  }

  /** Pixel height; unset = inherit a sheet-level default. */
  get heightPx(): Option<number> {
    return sheetStores(this.#sheet).rowMetadata.get(this.id).heightPx ?? null;
  }

  /**
   * The row's free-form attribute bag (the `attributes JSONB` column on
   * `SheetRowDb`), as the JSON it is. This is not a CellValue bag — it's
   * row-specific configuration, and each reader narrows the keys it
   * knows with the `Json` readers (`Json.string(row.attributes(),
   * "unit") ?? "kg"`) or decodes the whole bag with `attributesAs`.
   *
   * Same signature as the core `SheetRow.attributes()`, so one call site
   * reads either row type (`TreeRowLike`). Today this returns `{}`
   * rather than `null` for a row with no bag; callers still handle
   * `null` so a row read through the core twin agrees.
   */
  attributes(): Option<JsonObject> {
    return sheetStores(this.#sheet).rowMetadata.get(this.id).attributes ?? EMPTY_FROZEN_OBJECT;
  }

  /**
   * The attribute bag decoded by `decode` — the named boundary where a
   * reader turns JSON into its own shape. `null` when the row has no bag
   * or `decode` rejects it.
   *
   * Example: `row.attributesAs(TimezoneAttrs.decode)?.tz`.
   */
  attributesAs<A>(decode: (attributes: JsonObject) => Option<A>): Option<A> {
    const attributes = this.attributes();
    return attributes == null ? null : decode(attributes);
  }

  /**
   * The closures a preset-expanded row carries beside its attributes (a
   * data canvas's x-domain mapping, a builtin series' extractors — see
   * `Graph.Hooks`). `null` for rows no preset expanded.
   */
  get graphHooks(): Option<Graph.Hooks> {
    return sheetStores(this.#sheet).rowMetadata.get(this.id).graph ?? null;
  }

  /**
   * Walk this row's children for an instance of `ctor`. Used by the
   * plugin-dispatch contract: a `SourceRow`, a `LayoutRow`, or any
   * other `ModelRow` subclass that declares a hidden renderer (or
   * alias, etc.) child can resolve the typed child via this method.
   * Returns `null` when no matching child is installed.
   *
   * Lives on `ModelRow` (not `SourceRow`) so layout rows with
   * childSpec-declared renderer children — timeline, calendar-month,
   * etc. — participate in the same dispatch contract.
   *
   * Subclasses that ARE themselves the typed class for a plugin
   * (e.g. `AliasRow` subclasses, a `RendererRow` used as a plugin's
   * row input) override this to return `this` rather than walk
   * children.
   */
  defaultRenderer<T>(
    ctor: new (...args: never[]) => T,
  ): Option<T> {
    for (const child of this.children) {
      if (child instanceof ctor) return child;
    }
    return null;
  }

  /**
   * Capability flags. The base implementation returns a permissive
   * default; variants may override to tighten based on descriptor
   * invariants (e.g. alias children can't reparent).
   */
  capabilities(): Capabilities {
    return DEFAULT_CAPABILITIES;
  }

  /**
   * Whether drawer/property-list tree UIs should start this row
   * collapsed when it has children. Runtime UI state may still
   * override this default after the first bind.
   */
  get defaultCollapsedInPropertyList(): boolean {
    return false;
  }

  /**
   * How this row appears in the property list's `default` (simplified)
   * projection — `"branch"` (shown, children recurse), `"leaf"` (shown,
   * children folded away), or `"omit"` (removed). The base is the normal
   * `"branch"`; aliases and synthetic default renderers override. `full`
   * mode ignores this. See `PropertyListProjection.projectChildren`.
   */
  get defaultProjection(): PropertyListProjection.DefaultProjection {
    return "branch";
  }

  /**
   * Options this row declares it can configure. The base declares none;
   * variants override to expose their config — renderer attrs, layout
   * config, alias props, source metadata. A visible row's full options
   * surface is COMPOSED from its own contributions plus every
   * descendant's via `ModelRowOptions.composeSheetRow`, so a subsuming
   * alias/layout exposes its children's options without hardcoding them.
   *
   * Each contribution declares its own `tier`/`priority` (the placement
   * decision is decentralized, mirroring `defaultCollapsedInPropertyList`).
   *
   * Design: lona-sdk-2.0.project.docs/content/row-options-composition.md.
   */
  optionContributions(): readonly ModelRowOptions.Contribution[] {
    return [];
  }

  /**
   * Every interaction available on this row — composed options, intrinsic
   * mutations (title / height / delete), and capability actions (alias
   * reset / edit-as-group, source lookup-key) — as one flat, DOM-free list a
   * consumer can interrogate. Read only: an envelope is DRIVEN through the
   * SDK row that owns this model row (`sdkRow.envelopes.apply(msg, actor)`,
   * `@tento/lona`), which runs the checked write and mirrors what it wrote.
   * The same list as `RowEnvelopes.forRow(this)`.
   */
  envelopes(): readonly RowEnvelopes.Envelope[] {
    return sheetInternals(this.#sheet).envelopesFor(this);
  }

  /**
   * Ensure this row and every descendant has its cells for `range`: a source
   * row fetches its own (`RangeBasedFetcher`: padding + dedup), a formula
   * loads the rows it depends on and recomputes, an aggregate batches its
   * constituents. Products call this once per visible viewport refresh and
   * don't need to know about facades, encoding, canonical keys, or which
   * descendant rows hold cells.
   *
   * One call is one load pass (`./row-load.ts`): each row loads at most once,
   * and a formula moved under a row it depends on — or any other loop through
   * the tree and the dependency graph — settles instead of recursing. Variants
   * declare how they load (`declareRowLoader`); this is the only entry.
   */
  async ensureLoaded(
    range: NaiveDate.Range,
    padding: Option<DateUnit>,
  ): Promise<void> {
    await RowLoadPass.load(this, range, padding);
  }

  /**
   * Every cell this row holds in `range` (every cell it holds, unbounded,
   * when `range` is `null`), date-keyed and timepoint in one tagged list. A
   * synchronous read of the loaded stores. A row that holds no cells of its
   * own (a layout, alias, inert or renderer row) answers `[]`; `SourceRow`
   * answers its cells.
   */
  peekCells(_range: Option<NaiveDate.Range>): readonly SourceRow.Cell[] {
    return [];
  }


  /**
   * Snapshot the row's externally-observable state for tests +
   * debugging. The base returns `{ type, role, label? }`; variants
   * override to add their own fields (`unit`, `expression`,
   * `template`, `attrs`, …) by spreading `super.toDebug()` then
   * merging their keys.
   */
  toDebug(): Record<string, unknown> {
    return {
      type: this.constructor.name,
      role: this.descriptor.role,
      ...(this.label !== undefined ? { label: this.label } : {}),
    };
  }

  // The row's local-model edits (label, height, parent, delete) are not on
  // the public row: they change this sheet's copy only and resolve with no
  // server verdict. The persisting verbs are the SDK's
  // (`handle.rows.delete(row, actor)`, `sdkRow.update({ label }, actor)`,
  // `sdkRow.envelopes.apply(msg, actor)`); the local edits are
  // `RowLocalEdits` (`@tento-lona/sheets/internal-sdk`, and
  // `@tento-lona/sheets/internal-testing` for this repository's tests).
}

/**
 * The live row behind `row` — for a render front-end's binding pipeline
 * (`RenderBinding`, plan rows, a renderer's `ensureLoaded`), which reads the
 * row's variant. A friend door on the package-private friend entries (shipped, not
 * exported: `@tento-lona/sheets/internal-ui` for the front-ends,
 * `…/internal-sdk` for the SDK layer) and on the workspace-only
 * `…/internal-testing` for tests, never on an exported one: a
 * front-end that renders a sheet from its reads (`platformOf(handle).sheet`)
 * names the step where it takes the live row; application code has no use
 * for it and writes through the checked facade verbs. Resolves a reads face
 * (`rowReadsOf`) through the face's own record (`faceRowOf`), never by
 * `instanceof` on the face; a live row answers itself.
 */
export function liveRowOf(row: ModelRowReads): ModelRow {
  const live = faceRowOf(row);
  if (live != null) return live;
  if (row instanceof ModelRow) return row;
  throw new TypeError("liveRowOf: not a row read off a rows-side sheet");
}

const DEFAULT_CAPABILITIES: Capabilities = Object.freeze({
  canReparent: true,
  canDuplicate: true,
  canDelete: true,
  canReorder: true,
  canEditHeight: true,
  canEditTitle: true,
  canWriteCells: true,
});
